/* UTM link builder. The link rules and checks are plain functions so the
   tests and the server-rendered first paint (utm-builder.cjs) use the same
   code the browser runs. Nothing here fetches or opens the address typed in. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.UtmBuilder = api;
  if (typeof document !== 'undefined' && typeof document.querySelector === 'function') {
    const start = () => api.init(document.querySelector('[data-utm-lab]'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const TAGS = Object.freeze(['source', 'medium', 'campaign', 'content', 'term']);
  const REQUIRED = Object.freeze(['source', 'medium', 'campaign']);
  // Where the link goes out decides the suggested source and medium. Email and
  // SMS each have a GA4 channel to aim for; the other uses only report the
  // likely channel, and links on the store itself should carry no tags at all.
  const USES = Object.freeze({
    email: Object.freeze({ source: 'newsletter', medium: 'email', expect: 'Email' }),
    sms: Object.freeze({ source: 'sms', medium: 'sms', expect: 'SMS' }),
    social: Object.freeze({ source: 'instagram', medium: 'social', expect: null }),
    website: Object.freeze({ source: 'partner-site', medium: 'referral', expect: null }),
    print: Object.freeze({ source: 'packaging', medium: 'qr', expect: null }),
    onsite: Object.freeze({ source: 'website', medium: 'popup', expect: null })
  });
  const OTHER_USES = Object.freeze(['social', 'website', 'print', 'onsite']);
  // The Source help follows the destination: texts usually come from a
  // texting app. The other uses keep the email wording.
  const SOURCE_HELP = Object.freeze({
    email: 'Who sent it: your list or app, like <code>newsletter</code> or <code>klaviyo</code>.',
    sms: 'Who sent it: your texting app, like <code>postscript</code>, or just <code>sms</code>.'
  });
  const DEFAULTS = Object.freeze({
    url: 'https://solara.example/products/daily-cleanser',
    channel: 'email',
    other: 'social',
    source: 'newsletter',
    medium: 'email',
    campaign: 'Cleanser Restock',
    content: '',
    term: '',
    tidy: true
  });

  // Google Analytics 4 default channel group, manual-traffic rules, in the
  // order Google lists them: the first rule that matches names the channel.
  // https://support.google.com/analytics/answer/9756891
  const PAID = /^(.*cp.*|ppc|retargeting|paid.*)$/;
  // Google's campaign rule is ^(.*(([^a-df-z]|^)shop|shopping).*)$ and its
  // rules ignore case, so it runs on lowercased text.
  const SHOP_CAMPAIGN = /(?:^|[^a-df-z])shop|shopping/;
  const EMAIL_WORDS = Object.freeze(['email', 'e-mail', 'e_mail', 'e mail']);
  const SOCIAL_MEDIUMS = Object.freeze(['social', 'social-network', 'social-media', 'sm', 'social network', 'social media']);
  const DISPLAY_MEDIUMS = Object.freeze(['display', 'banner', 'expandable', 'interstitial', 'cpm']);
  // Google's published source categories (819 names; downloaded 2026-09-26 from
  // the spreadsheet linked on the page above). A source on these lists wins
  // over Email and SMS: utm_source=shopify lands in Organic Shopping.
  const SOURCE_LISTS = {
    shopping: 'aax-us-east.amazon-adsystem.com|aax.amazon-adsystem.com|alibaba|alibaba.com|amazon|amazon.co.uk|amazon.com|apps.shopify.com|checkout.shopify.com|checkout.stripe.com|cr.shopping.naver.com|cr2.shopping.naver.com|ebay|ebay.co.uk|ebay.com|ebay.com.au|ebay.de|etsy|etsy.com|google shopping|igshopping|m.alibaba.com|m.shopping.naver.com|mercadolibre|mercadolibre.com|mercadolibre.com.ar|mercadolibre.com.mx|message.alibaba.com|msearch.shopping.naver.com|nl.shopping.net|no.shopping.net|offer.alibaba.com|one.walmart.com|order.shopping.yahoo.co.jp|partners.shopify.com|s3.amazonaws.com|se.shopping.net|shop.app|shopify|shopify.com|shopping.naver.com|shopping.yahoo.co.jp|shopping.yahoo.com|shopzilla|shopzilla.com|simplycodes.com|store.shopping.yahoo.co.jp|stripe|stripe.com|uk.shopping.net|walmart|walmart.com',
    social: '43things|43things.com|51.com|5ch.net|academia.edu|activerain|activerain.com|activeworlds|activeworlds.com|addthis|addthis.com|airg.ca|allnurses.com|allrecipes.com|alumniclass|alumniclass.com|ameba.jp|ameblo.jp|americantowns|americantowns.com|amp.reddit.com|ancestry.com|anobii|anobii.com|answerbag|answerbag.com|answers.yahoo.com|aolanswers|aolanswers.com|apps.facebook.com|ar.pinterest.com|artstation.com|askubuntu|askubuntu.com|asmallworld.com|athlinks|athlinks.com|away.vk.com|awe.sm|b.hatena.ne.jp|baby-gaga|baby-gaga.com|babyblog.ru|badoo|badoo.com|bebo|bebo.com|beforeitsnews|beforeitsnews.com|bharatstudent|bharatstudent.com|biip.no|biswap.org|bit.ly|blackcareernetwork.com|blackplanet|blackplanet.com|blip.fm|blog.com|blog.feedspot.com|blog.goo.ne.jp|blog.naver.com|blog.yahoo.co.jp|blogg.no|bloggang.com|blogger|blogger.com|blogher|blogher.com|bloglines|bloglines.com|blogs.com|blogsome|blogsome.com|blogspot|blogspot.com|blogster|blogster.com|blurtit|blurtit.com|bookmarks.yahoo.co.jp|bookmarks.yahoo.com|br.pinterest.com|brightkite|brightkite.com|brizzly|brizzly.com|business.facebook.com|buzzfeed|buzzfeed.com|buzznet|buzznet.com|cafe.naver.com|cafemom|cafemom.com|camospace|camospace.com|canalblog.com|care.com|care2|care2.com|caringbridge.org|catster|catster.com|cbnt.io|cellufun|cellufun.com|centerblog.net|chat.zalo.me|chegg.com|chicagonow|chicagonow.com|chiebukuro.yahoo.co.jp|classmates|classmates.com|classquest|classquest.com|co.pinterest.com|cocolog-nifty|cocolog-nifty.com|copainsdavant.linternaute.com|couchsurfing.org|cozycot|cozycot.com|cross.tv|crunchyroll|crunchyroll.com|cyworld|cyworld.com|cz.pinterest.com|d.hatena.ne.jp|dailystrength.org|deluxe.com|deviantart|deviantart.com|dianping|dianping.com|digg|digg.com|diigo|diigo.com|discover.hubpages.com|disqus|disqus.com|dogster|dogster.com|dol2day|dol2day.com|doostang|doostang.com|dopplr|dopplr.com|douban|douban.com|draft.blogger.com|draugiem.lv|drugs-forum|drugs-forum.com|dzone|dzone.com|edublogs.org|elftown|elftown.com|epicurious.com|everforo.com|exblog.jp|extole|extole.com|facebook|facebook.com|faceparty|faceparty.com|fandom.com|fanpop|fanpop.com|fark|fark.com|fb|fb.me|fc2|fc2.com|feedspot|feministing|feministing.com|filmaffinity|filmaffinity.com|flickr|flickr.com|flipboard|flipboard.com|folkdirect|folkdirect.com|foodservice|foodservice.com|forums.androidcentral.com|forums.crackberry.com|forums.imore.com|forums.nexopia.com|forums.webosnation.com|forums.wpcentral.com|fotki|fotki.com|fotolog|fotolog.com|foursquare|foursquare.com|free.facebook.com|friendfeed|friendfeed.com|fruehstueckstreff.org|fubar|fubar.com|gaiaonline|gaiaonline.com|gamerdna|gamerdna.com|gather.com|geni.com|getpocket.com|glassboard|glassboard.com|glassdoor|glassdoor.com|godtube|godtube.com|goldenline.pl|goldstar|goldstar.com|goo.gl|gooblog|goodreads|goodreads.com|google+|googlegroups.com|googleplus|govloop|govloop.com|gowalla|gowalla.com|gree.jp|groups.google.com|gulli.com|gutefrage.net|habbo|habbo.com|hatena|hi5|hi5.com|hootsuite|hootsuite.com|houzz|houzz.com|hoverspot|hoverspot.com|hr.com|hu.pinterest.com|hubculture|hubculture.com|hubpages.com|hyves.net|hyves.nl|ibibo|ibibo.com|id.pinterest.com|identi.ca|ig|imageshack|imageshack.com|imageshack.us|imvu|imvu.com|in.pinterest.com|insanejournal|insanejournal.com|instagram|instagram.com|instapaper|instapaper.com|internations.org|interpals.net|intherooms|intherooms.com|irc-galleria.net|is.gd|italki|italki.com|jammerdirect|jammerdirect.com|jappy.com|jappy.de|kaboodle.com|kakao|kakao.com|kakaocorp.com|kaneva|kaneva.com|kin.naver.com|l.facebook.com|l.instagram.com|l.messenger.com|last.fm|librarything|librarything.com|lifestream.aol.com|line|line.me|linkedin|linkedin.com|listal|listal.com|listography|listography.com|livedoor.com|livedoorblog|livejournal|livejournal.com|lm.facebook.com|lnkd.in|m.blog.naver.com|m.cafe.naver.com|m.facebook.com|m.kin.naver.com|m.vk.com|m.yelp.com|mbga.jp|medium.com|meetin.org|meetup|meetup.com|meinvz.net|meneame.net|menuism.com|messages.google.com|messages.yahoo.co.jp|messenger|messenger.com|mix.com|mixi.jp|mobile.facebook.com|mocospace|mocospace.com|mouthshut|mouthshut.com|movabletype|movabletype.com|mubi|mubi.com|my.opera.com|myanimelist.net|myheritage|myheritage.com|mylife|mylife.com|mymodernmet|mymodernmet.com|myspace|myspace.com|netvibes|netvibes.com|news.ycombinator.com|newsshowcase|nexopia|ngopost.org|niconico|nicovideo.jp|nightlifelink|nightlifelink.com|ning|ning.com|nl.pinterest.com|odnoklassniki.ru|odnoklassniki.ua|okwave.jp|old.reddit.com|oneworldgroup.org|onstartups|onstartups.com|opendiary|opendiary.com|oshiete.goo.ne.jp|out.reddit.com|over-blog.com|overblog.com|paper.li|partyflock.nl|photobucket|photobucket.com|pinboard|pinboard.in|pingsta|pingsta.com|pinterest|pinterest.at|pinterest.ca|pinterest.ch|pinterest.cl|pinterest.co.kr|pinterest.co.uk|pinterest.com|pinterest.com.au|pinterest.com.mx|pinterest.de|pinterest.es|pinterest.fr|pinterest.it|pinterest.jp|pinterest.nz|pinterest.ph|pinterest.pt|pinterest.ru|pinterest.se|pixiv.net|pl.pinterest.com|playahead.se|plurk|plurk.com|plus.google.com|plus.url.google.com|pocket.co|posterous|posterous.com|pro.homeadvisor.com|pulse.yahoo.com|qapacity|qapacity.com|quechup|quechup.com|quora|quora.com|qzone.qq.com|ravelry|ravelry.com|reddit|reddit.com|redux|redux.com|renren|renren.com|researchgate.net|reunion|reunion.com|reverbnation|reverbnation.com|rtl.de|ryze|ryze.com|salespider|salespider.com|scoop.it|screenrant|screenrant.com|scribd|scribd.com|scvngr|scvngr.com|secondlife|secondlife.com|serverfault|serverfault.com|shareit|sharethis|sharethis.com|shvoong.com|sites.google.com|skype|skyrock|skyrock.com|slashdot.org|slideshare.net|smartnews.com|snapchat|snapchat.com|social|sociallife.com.br|socialvibe|socialvibe.com|spaces.live.com|spoke|spoke.com|spruz|spruz.com|ssense.com|stackapps|stackapps.com|stackexchange|stackexchange.com|stackoverflow|stackoverflow.com|stardoll.com|stickam|stickam.com|studivz.net|suomi24.fi|superuser|superuser.com|sweeva|sweeva.com|t.co|t.me|tagged|tagged.com|taggedmail|taggedmail.com|talkbiznow|talkbiznow.com|taringa.net|techmeme|techmeme.com|tencent|tencent.com|tiktok|tiktok.com|tinyurl|tinyurl.com|toolbox|toolbox.com|touch.facebook.com|tr.pinterest.com|travellerspoint|travellerspoint.com|tripadvisor|tripadvisor.com|trombi|trombi.com|trustpilot|tudou|tudou.com|tuenti|tuenti.com|tumblr|tumblr.com|tweetdeck|tweetdeck.com|twitter|twitter.com|twoo.com|typepad|typepad.com|unblog.fr|urbanspoon.com|ushareit.com|ushi.cn|vampirefreaks|vampirefreaks.com|vampirerave|vampirerave.com|vg.no|video.ibm.com|vk.com|vkontakte.ru|wakoopa|wakoopa.com|wattpad|wattpad.com|web.facebook.com|web.skype.com|webshots|webshots.com|wechat|wechat.com|weebly|weebly.com|weibo|weibo.com|wer-weiss-was.de|weread|weread.com|whatsapp|whatsapp.com|wiki.answers.com|wikihow.com|wikitravel.org|woot.com|wordpress|wordpress.com|wordpress.org|xanga|xanga.com|xing|xing.com|yahoo-mbga.jp|yammer|yammer.com|yelp|yelp.co.uk|yelp.com|youroom.in|za.pinterest.com|zalo|zoo.gr|zooppa|zooppa.com',
    search: '360.cn|alice|aol|ar.search.yahoo.com|ask|at.search.yahoo.com|au.search.yahoo.com|auone|avg|babylon|baidu|biglobe|biglobe.co.jp|biglobe.ne.jp|bing|br.search.yahoo.com|ca.search.yahoo.com|centrum.cz|ch.search.yahoo.com|cl.search.yahoo.com|cn.bing.com|cnn|co.search.yahoo.com|comcast|conduit|daum|daum.net|de.search.yahoo.com|dk.search.yahoo.com|dogpile|dogpile.com|duckduckgo|ecosia.org|email.seznam.cz|eniro|es.search.yahoo.com|espanol.search.yahoo.com|exalead.com|excite.com|fi.search.yahoo.com|firmy.cz|fr.search.yahoo.com|globo|go.mail.ru|google|google-play|hk.search.yahoo.com|id.search.yahoo.com|in.search.yahoo.com|incredimail|it.search.yahoo.com|kvasir|lens.google.com|lite.qwant.com|lycos|m.baidu.com|m.naver.com|m.search.naver.com|m.sogou.com|mail.rambler.ru|mail.yandex.ru|malaysia.search.yahoo.com|msn|msn.com|mx.search.yahoo.com|najdi|naver|naver.com|news.google.com|nl.search.yahoo.com|no.search.yahoo.com|ntp.msn.com|nz.search.yahoo.com|onet|onet.pl|pe.search.yahoo.com|ph.search.yahoo.com|pl.search.yahoo.com|play.google.com|qwant|qwant.com|rakuten|rakuten.co.jp|rambler|rambler.ru|se.search.yahoo.com|search-results|search.aol.co.uk|search.aol.com|search.google.com|search.smt.docomo.ne.jp|search.ukr.net|secureurl.ukr.net|seznam|seznam.cz|sg.search.yahoo.com|so.com|sogou|sogou.com|sp-web.search.auone.jp|startsiden|startsiden.no|suche.aol.de|terra|th.search.yahoo.com|tr.search.yahoo.com|tut.by|tw.search.yahoo.com|uk.search.yahoo.com|ukr|us.search.yahoo.com|virgilio|vn.search.yahoo.com|wap.sogou.com|webmaster.yandex.ru|websearch.rakuten.co.jp|yahoo|yahoo.co.jp|yahoo.com|yandex|yandex.by|yandex.com|yandex.com.tr|yandex.fr|yandex.kz|yandex.ru|yandex.ua|yandex.uz|zen.yandex.ru',
    video: 'blog.twitch.tv|crackle|crackle.com|curiositystream|curiositystream.com|d.tube|dailymotion|dailymotion.com|dashboard.twitch.tv|disneyplus|disneyplus.com|fast.wistia.net|help.hulu.com|help.netflix.com|hulu|hulu.com|id.twitch.tv|iq.com|iqiyi|iqiyi.com|jobs.netflix.com|justin.tv|m.twitch.tv|m.youtube.com|music.youtube.com|netflix|netflix.com|player.twitch.tv|player.vimeo.com|ted|ted.com|twitch|twitch.tv|utreon|utreon.com|veoh|veoh.com|viadeo.journaldunet.com|vimeo|vimeo.com|wistia|wistia.com|youku|youku.com|youtube|youtube.com'
  };
  let sourceSets = null;

  // {first_name}, {{ campaign.name }}, Mailchimp's *|CAMPAIGN_UID|* and
  // Omnisend's [[contact.first_name]]: sending apps fill these in, so they are
  // neither tidied nor percent-encoded (encoded braces stop the fill-in).
  const MERGE_TAG = /\{\{[^{}]*\}\}|\{\s*[A-Za-z_][\w.-]*\s*\}|\*\|[^|*\n]+\|\*|\[\[[^[\]\n]*\]\]/g;
  const PERSONAL_TAG = /(?:^|[\s{.|[])(first_?name|last_?name|full_?name|f_?name|l_?name|e-?mail|phone(?:_?number)?)(?=[\s|}.\]]|$)/i;
  const EMAIL_ADDRESS = /[^\s@{}:/?#&=]+@[^\s@{}:/?#&=]+\.[a-z]{2,}/i;
  // Tracking IDs an address picks up when it's copied from the browser after
  // a click. The group decides the check: a subscriber ID tracks everyone who
  // clicks as that one person, a Google ad click ID can move the credit to the
  // ad, and the rest are leftovers that are safe to remove.
  const TRACKING_IDS = new Map([
    ['_kx', { group: 'subscriber', by: 'Klaviyo' }],
    ['mc_eid', { group: 'subscriber', by: 'Mailchimp' }],
    ['gclid', { group: 'ad', by: 'Google Ads' }],
    ['dclid', { group: 'ad', by: 'Google' }],
    ['gbraid', { group: 'ad', by: 'Google Ads' }],
    ['wbraid', { group: 'ad', by: 'Google Ads' }],
    ['fbclid', { group: 'click', by: 'Facebook or Instagram' }],
    ['msclkid', { group: 'click', by: 'Microsoft Ads' }],
    ['ttclid', { group: 'click', by: 'TikTok' }],
    ['srsltid', { group: 'click', by: 'Google' }],
    ['_pos', { group: 'click', by: 'Shopify’s search' }],
    ['_sid', { group: 'click', by: 'Shopify’s search' }],
    ['_ss', { group: 'click', by: 'Shopify’s search' }]
  ]);
  // htps://, https//, http// and a doubled https://https:// are typos for
  // https://. The URL parser reads "https//shop.com" as a path, not a site.
  const SCHEME_LIKE = /^(h{1,2}t{0,3}p{1,2}s{0,2}|t{1,3}p{1,2}s{0,2})([:;]\/{0,3}|\/{1,3})/i;
  const APOSTROPHES = /['‘’]/g;
  const NOT_WEB = Object.freeze(['mailto', 'tel', 'sms', 'javascript', 'data', 'file', 'ftp', 'blob']);
  const unicodePattern = (source, flags, fallback) => {
    try { return new RegExp(source, flags); } catch (_) { return fallback; }
  };
  const NOT_WORD = unicodePattern('[^\\p{L}\\p{N}._-]+', 'gu', /[^A-Za-z0-9\u00c0-\u024f._-]+/g);
  const UPPER = unicodePattern('\\p{Lu}', 'u', /[A-Z\u00c0-\u00de]/);
  const SPACE = /[\s\u00a0\u2000-\u200b\u202f\u205f\u3000]/;
  // Characters RFC 3986 allows in a query or fragment. Anything else in an
  // existing parameter is percent-encoded; its spelling is otherwise kept.
  const URL_SAFE = /[A-Za-z0-9\-._~!$&'()*+,;=:@/?]/;
  const GSM_BASIC = new Set(Array.from('@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'));
  const GSM_EXTENDED = new Set(Array.from('\f^{}\\[~]|€'));
  const CHAR_NAMES = { ' ': 'space', '\u00a0': 'no-break space', '\t': 'tab' };

  const esc = value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const code = value => `<code>${esc(value)}</code>`;
  const quote = value => `“${esc(value)}”`;
  const plural = (count, one, many = `${one}s`) => `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;
  const joinWords = words => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
  const text = value => (value == null ? '' : String(value));

  function wellFormed(value) {
    return value.replace(/[\ud800-\udbff](?![\udc00-\udfff])|(?:^|[^\ud800-\udbff])[\udc00-\udfff]/g, match => match.replace(/[\ud800-\udfff]/, '\ufffd'));
  }

  // encodeURIComponent leaves ! ' ( ) * alone. Phones and chat apps often end
  // a link at a quote or bracket, so tag values encode those too.
  function encodeText(value) {
    return encodeURIComponent(wellFormed(value)).replace(/[!'()*]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  }

  function splitMergeTags(value) {
    const parts = [];
    let last = 0;
    String(value).replace(MERGE_TAG, (match, offset) => {
      if (offset > last) parts.push({ text: value.slice(last, offset), tag: false });
      parts.push({ text: match, tag: true });
      last = offset + match.length;
      return match;
    });
    if (last < value.length) parts.push({ text: value.slice(last), tag: false });
    return parts;
  }

  // Lowercase, and one hyphen for each run of spaces or punctuation, as in
  // Google's own example utm_campaign=summer-sale. Underscores, periods and
  // letters from any language stay: they are consistent and readable.
  // Apostrophes go first, so "Mother's Day" is mothers-day, not mother-s-day.
  function tidyValue(value) {
    const parts = splitMergeTags(text(value).trim());
    const joined = parts.map(part => (part.tag ? part.text : part.text.toLowerCase().replace(APOSTROPHES, '').replace(NOT_WORD, '-'))).join('');
    return joined.replace(/-{2,}/g, '-').replace(/^[-._]+|[-._]+$/g, '');
  }

  function encodeValue(value) {
    return splitMergeTags(text(value)).map(part => (part.tag ? part.text : encodeText(part.text))).join('');
  }

  // The characters a value had to change to travel in a link, once each.
  function encodedCharacters(value) {
    const seen = new Map();
    splitMergeTags(text(value)).filter(part => !part.tag).forEach(part => {
      Array.from(part.text).forEach(char => {
        const encoded = encodeText(char);
        if (encoded !== char && !seen.has(char)) seen.set(char, encoded);
      });
    });
    return [...seen].map(([char, encoded]) => ({ char, name: CHAR_NAMES[char] || char, encoded }));
  }

  const hasUpper = value => splitMergeTags(text(value)).some(part => !part.tag && UPPER.test(part.text));
  const mergeTags = value => splitMergeTags(text(value)).filter(part => part.tag).map(part => part.text);

  function decodePart(value) {
    try { return decodeURIComponent(value.replace(/\+/g, ' ')); } catch (_) { return value; }
  }

  function sanitize(part) {
    const chars = Array.from(part);
    let out = '';
    for (let index = 0; index < chars.length; index += 1) {
      const char = chars[index];
      if (char === '%') {
        const hex = chars.slice(index + 1, index + 3).join('');
        if (/^[0-9A-Fa-f]{2}$/.test(hex)) { out += `%${hex}`; index += 2; } else out += '%25';
      } else out += URL_SAFE.test(char) ? char : encodeText(char);
    }
    return out;
  }

  // "htps://shop.com" or "https//shop.com": the mistyped start and the address
  // with https:// in its place, or null when the start is fine. "https:/shop.com"
  // counts as fine: the URL parser reads it as https://shop.com.
  function schemeTypo(typed) {
    let rest = typed;
    let count = 0;
    let typo = false;
    for (let match = SCHEME_LIKE.exec(rest); match; match = SCHEME_LIKE.exec(rest)) {
      if (!/^https?$/i.test(match[1]) || match[2][0] !== ':') typo = true;
      rest = rest.slice(match[0].length);
      count += 1;
    }
    if (!count || !rest || (count === 1 && !typo)) return null;
    return { typo: typed.slice(0, typed.length - rest.length), fix: `https://${rest}` };
  }

  // "Link: https://shop.com/tee" or "Shop now https://…": text before the
  // address, and the address on its own. A URL after "=" is a parameter's
  // value (?return_to=https://…), not pasted text.
  function textBefore(typed) {
    const later = /https?:\/\//i.exec(typed);
    if (!later || later.index === 0) return null;
    const before = typed.slice(0, later.index);
    if (!/[\s:(<"'“‘[]$/.test(before)) return null;
    const fix = typed.slice(later.index).split(/\s/)[0].replace(/[>)\]"'”’.,;:!?]+$/, '');
    return { before: before.trim(), fix };
  }

  // Two links pasted together ("https://a.com/teehttps://a.com/hat"): the
  // first address on its own, and the https:// that starts the second.
  function joinedAddress(typed) {
    const pattern = /https?:\/\//gi;
    pattern.lastIndex = 1;
    for (let match = pattern.exec(typed); match; match = pattern.exec(typed)) {
      if (typed[match.index - 1] === '=') continue;
      const first = typed.slice(0, match.index).replace(/[\s,;|]+$/, '');
      return { first, second: match[0] };
    }
    return null;
  }

  // Split the address by hand so the store's own parameters and #fragment keep
  // their spelling; only the scheme, host and path go through the URL parser.
  function parsePage(input) {
    // Browsers drop tabs and line breaks from a pasted address, and so do we.
    const typed = text(input).replace(/[\t\n\r]/g, '').trim();
    if (!typed) return { ok: false, error: 'empty' };
    const typo = schemeTypo(typed);
    if (typo) return { ok: false, error: 'scheme-typo', typo: typo.typo, fix: typo.fix };
    const prefixed = textBefore(typed);
    if (prefixed) return { ok: false, error: 'text-before', before: prefixed.before, fix: prefixed.fix };
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(typed);
    const protocol = scheme ? scheme[1].toLowerCase() : '';
    // "shop.example:8080/page" has a port, not a scheme.
    const hostWithPort = scheme && !NOT_WEB.includes(protocol) && /^\d/.test(typed.slice(scheme[0].length));
    if (!scheme || hostWithPort) {
      const candidate = typed.replace(/^\/+/, '');
      const host = candidate.split(/[/?#]/)[0];
      return /^[^\s.@]+(\.[^\s.@]+)+(:\d+)?$/.test(host) ? { ok: false, error: 'no-scheme', fix: `https://${candidate}` } : { ok: false, error: 'invalid' };
    }
    if (protocol !== 'http' && protocol !== 'https') return { ok: false, error: 'scheme', scheme: protocol };
    const hashAt = typed.indexOf('#');
    const beforeHash = hashAt === -1 ? typed : typed.slice(0, hashAt);
    const fragment = hashAt === -1 ? '' : typed.slice(hashAt + 1);
    const queryAt = beforeHash.indexOf('?');
    const address = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
    const query = queryAt === -1 ? '' : beforeHash.slice(queryAt + 1);
    let parsed;
    try { parsed = new URL(address); } catch (_) { return { ok: false, error: 'invalid' }; }
    if (!parsed.hostname) return { ok: false, error: 'invalid' };
    const kept = [];
    const removed = [];
    query.split('&').forEach(segment => {
      if (!segment) return;
      const equals = segment.indexOf('=');
      const key = decodePart(equals === -1 ? segment : segment.slice(0, equals)).trim();
      const value = equals === -1 ? '' : decodePart(segment.slice(equals + 1));
      // Old tags are replaced, whatever their capitals, so the link carries one set.
      if (/^utm_/i.test(key)) removed.push({ key, value });
      else kept.push({ key, value, raw: sanitize(segment) });
    });
    const base = `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
    return {
      ok: true,
      protocol,
      host: parsed.hostname,
      base,
      kept,
      removed,
      fragment: sanitize(fragment),
      spaces: Array.from(typed).filter(char => SPACE.test(char)).length,
      fragmentTags: /(^|[?&#;])utm_[a-z_]*=/i.test(fragment),
      joined: joinedAddress(typed)
    };
  }

  function sourceCategory(source) {
    if (!sourceSets) sourceSets = Object.fromEntries(Object.entries(SOURCE_LISTS).map(([name, list]) => [name, new Set(list.split('|'))]));
    return Object.keys(sourceSets).find(name => sourceSets[name].has(source)) || null;
  }

  // Which default channel GA4 would likely give these tags, and the rule
  // that decided it. Channel rules ignore capitals; reports do not.
  function ga4Channel(tags = {}) {
    const low = value => text(value).trim().toLowerCase();
    const source = low(tags.source);
    const medium = low(tags.medium);
    const campaign = low(tags.campaign);
    const category = source ? sourceCategory(source) : null;
    const shop = SHOP_CAMPAIGN.exec(campaign);
    const shopWord = shop ? (/shopping/.test(shop[0]) ? 'shopping' : 'shop') : '';
    const paid = PAID.test(medium);
    // The rules ignore capitals, but the reason quotes each value as it
    // goes into the link: utm_medium=Email is “Email”, not “email”.
    const shown = { source: text(tags.source).trim(), medium: text(tags.medium).trim() };
    const found = (name, rule, extra = {}) => ({ name, rule, source, medium, campaign, category, shown, ...extra });
    if (source === '(direct)' && ['(none)', '(not set)'].includes(medium)) return found('Direct', 'direct');
    if (campaign.includes('cross-network')) return found('Cross-network', 'campaign-cross-network');
    if ((category === 'shopping' || shop) && paid) return found('Paid Shopping', category === 'shopping' ? 'paid-source-list' : 'paid-campaign-shop', { word: shopWord });
    if (category === 'search' && paid) return found('Paid Search', 'paid-source-list');
    if (category === 'social' && paid) return found('Paid Social', 'paid-source-list');
    if (category === 'video' && paid) return found('Paid Video', 'paid-source-list');
    if (DISPLAY_MEDIUMS.includes(medium)) return found('Display', 'medium-is');
    if (paid) return found('Paid Other', 'medium-paid');
    if (category === 'shopping' || shop) return found('Organic Shopping', category === 'shopping' ? 'source-list' : 'campaign-shop', { word: shopWord });
    if (category === 'social' || SOCIAL_MEDIUMS.includes(medium)) return found('Organic Social', category === 'social' ? 'source-list' : 'medium-is');
    if (category === 'video' || medium.includes('video')) return found('Organic Video', category === 'video' ? 'source-list' : 'medium-video');
    if (category === 'search' || medium === 'organic') return found('Organic Search', category === 'search' ? 'source-list' : 'medium-is');
    if (medium === 'ai-assistant') return found('AI Assistant', 'medium-is');
    if (['referral', 'app', 'link'].includes(medium)) return found('Referral', 'medium-is');
    if (EMAIL_WORDS.includes(medium)) return found('Email', 'medium-is');
    if (EMAIL_WORDS.includes(source)) return found('Email', 'source-is');
    if (medium === 'affiliate' || medium === 'audio') return found(medium === 'audio' ? 'Audio' : 'Affiliates', 'medium-is');
    if (medium === 'sms') return found('SMS', 'medium-exact');
    if (source === 'sms') return found('SMS', 'source-exact');
    const push = medium.endsWith('push') ? 'ends with “push”' : medium.includes('mobile') ? 'contains “mobile”' : medium.includes('notification') ? 'contains “notification”' : '';
    if (push || source === 'firebase') return found('Mobile Push Notifications', push ? 'medium-push' : 'source-firebase', { push });
    return found('Unassigned', 'unassigned');
  }

  function channelReason(channel) {
    const { rule, category } = channel;
    const source = channel.shown ? channel.shown.source : channel.source;
    const medium = channel.shown ? channel.shown.medium : channel.medium;
    if (rule === 'medium-is') return `utm_medium is ${quote(medium)}`;
    if (rule === 'source-is') return `utm_source is ${quote(source)}`;
    if (rule === 'medium-exact') return `utm_medium is exactly ${quote(medium)}`;
    if (rule === 'source-exact') return `utm_source is exactly ${quote(source)}`;
    if (rule === 'source-list') return `Google lists ${quote(source)} as a ${category} site`;
    if (rule === 'paid-source-list') return `Google lists ${quote(source)} as a ${category} site, and utm_medium ${quote(medium)} counts as paid`;
    if (rule === 'campaign-shop') return `the campaign name contains ${quote(channel.word)}`;
    if (rule === 'paid-campaign-shop') return `the campaign name contains ${quote(channel.word)}, and utm_medium ${quote(medium)} counts as paid`;
    if (rule === 'medium-paid') return `utm_medium ${quote(medium)} counts as a paid ad`;
    if (rule === 'medium-video') return `utm_medium contains ${quote('video')}`;
    if (rule === 'medium-push') return `utm_medium ${channel.push}`;
    if (rule === 'source-firebase') return `utm_source is ${quote(source)}`;
    if (rule === 'campaign-cross-network') return `the campaign name contains ${quote('cross-network')}`;
    if (rule === 'direct') return `utm_source is ${quote(source)}`;
    return medium ? `no default rule matches utm_medium ${quote(medium)}` : 'the link has no utm_medium';
  }

  // Where an unexpected channel came from decides the fix: an earlier rule
  // (a listed source, "shop" in the campaign) or a medium no rule expects.
  function channelFix(channel, expected, use) {
    const suggested = USES[use];
    // Would this medium reach the expected channel with a source no rule
    // knows? If not, the medium is the fix; if so, an earlier rule won.
    const own = ga4Channel({ source: 'x', medium: channel.medium, campaign: '' }).name === expected;
    if (!own) return `Set utm_medium to ${code(suggested.medium)} for the ${esc(expected)} channel.`;
    const earlier = `Google checks ${esc(channel.name)} before ${esc(expected)}.`;
    if (/campaign-shop$/.test(channel.rule)) return `${earlier} Leave ${quote(channel.word)} out of the campaign name.`;
    if (channel.rule === 'campaign-cross-network') return `${earlier} Leave ${quote('cross-network')} out of the campaign name.`;
    if (/source-list$/.test(channel.rule)) return `${earlier} Use a source that isn’t on Google’s list, such as ${code(suggested.source)}.`;
    if (channel.rule === 'source-is') return `${earlier} Change utm_source from ${quote(channel.shown ? channel.shown.source : channel.source)}.`;
    return `${earlier} Change utm_medium to ${code(suggested.medium)}.`;
  }

  // SMS length in GSM-7 units: ~ ^ { } [ ] \ | and € take 2 of the 160.
  function smsLength(value) {
    const chars = Array.from(text(value));
    const doubled = [...new Set(chars.filter(char => GSM_EXTENDED.has(char)))];
    const gsm = chars.every(char => GSM_BASIC.has(char) || GSM_EXTENDED.has(char));
    const units = gsm ? chars.reduce((sum, char) => sum + (GSM_EXTENDED.has(char) ? 2 : 1), 0) : text(value).length;
    return { characters: chars.length, units, gsm, doubled, limit: gsm ? 160 : 70 };
  }

  function normalizeState(input = {}) {
    const channel = ['email', 'sms', 'other'].includes(input.channel) ? input.channel : DEFAULTS.channel;
    const other = OTHER_USES.includes(input.other) ? input.other : DEFAULTS.other;
    const use = channel === 'other' ? other : channel;
    return {
      url: text(input.url),
      channel,
      other,
      use,
      source: input.source == null ? USES[use].source : text(input.source),
      medium: input.medium == null ? USES[use].medium : text(input.medium),
      campaign: text(input.campaign),
      content: text(input.content),
      term: text(input.term),
      tidy: input.tidy !== false
    };
  }

  function buildLink(input = {}) {
    const state = normalizeState(input);
    const page = parsePage(state.url);
    const tags = TAGS.map(name => {
      const typed = state[name].trim();
      const value = state.tidy ? tidyValue(typed) : typed;
      return {
        name,
        key: `utm_${name}`,
        typed,
        value,
        encoded: encodeValue(value),
        required: REQUIRED.includes(name),
        tidied: value !== typed,
        escapes: encodedCharacters(value),
        mergeTags: mergeTags(value)
      };
    });
    const byName = Object.fromEntries(tags.map(tag => [tag.name, tag]));
    const missing = tags.filter(tag => tag.required && !tag.value).map(tag => tag.name);
    const tagQuery = tags.filter(tag => tag.value).map(tag => `${tag.key}=${tag.encoded}`);
    let url = null;
    let plainUrl = null;
    if (page.ok) {
      const kept = page.kept.map(param => param.raw);
      const query = parts => (parts.length ? `?${parts.join('&')}` : '');
      const hash = page.fragment ? `#${page.fragment}` : '';
      url = page.base + query(kept.concat(tagQuery)) + hash;
      plainUrl = page.base + query(kept) + hash;
    }
    const channel = ga4Channel({ source: byName.source.value, medium: byName.medium.value, campaign: byName.campaign.value });
    const expected = USES[state.use].expect;
    const result = {
      state,
      use: state.use,
      page,
      tags,
      missing,
      url,
      plainUrl,
      channel,
      expected,
      channelState: !byName.medium.value && !byName.source.value ? 'waiting' : state.use === 'onsite' ? 'onsite' : expected && channel.name !== expected ? 'mismatch' : expected ? 'match' : 'neutral',
      length: url ? smsLength(url) : null,
      status: !page.ok ? 'invalid' : missing.length ? 'incomplete' : 'ready'
    };
    result.checks = checksFor(result);
    return result;
  }

  const MISSING_WORDS = { source: 'a source', medium: 'a medium', campaign: 'a campaign name' };
  const LEVEL_ORDER = { error: 0, warn: 1, info: 2, ok: 3 };
  // Shopify-only sentences carry the site's platform class, so a visitor who
  // sells on Wix reads the sentence without them.
  const SHOPIFY_CAMPAIGN = 'Shopify attributes a visit to marketing when the link has utm_campaign';
  const trackingId = key => TRACKING_IDS.get(text(key).trim().toLowerCase()) || null;
  const shorten = (value, max = 40) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

  // The tracking IDs in an address, one entry per group, each ID once.
  function trackingGroups(page) {
    const groups = new Map();
    page.kept.forEach(param => {
      const id = trackingId(param.key);
      if (!id) return;
      const group = groups.get(id.group) || groups.set(id.group, []).get(id.group);
      if (!group.some(entry => entry.key.toLowerCase() === param.key.toLowerCase())) group.push({ key: param.key, by: id.by });
    });
    return groups;
  }

  function checksFor(result) {
    const { page, tags, state } = result;
    const checks = [];
    const add = (id, level, title, detail, action, field) => checks.push({ id, level, title, detail, action, field });
    // Fixes to the address carry the new address, so a button only has to
    // put it in the box.
    const address = text(state.url).replace(/[\t\n\r]/g, '').trim();
    if (!page.ok) {
      // An empty box is a next step, not a mistake; the others are shown
      // under the address field itself (field: 'url').
      if (page.error === 'empty') add('url-empty', 'error', 'Add your page address', 'Paste the page people should land on, such as https://yourstore.com/products/item.');
      if (page.error === 'no-scheme') add('url-no-scheme', 'error', 'Add https:// to the start', 'Emails and texts need the full address. Without https://, a link can open the wrong page or not work at all.', { id: 'add-https', label: 'Add https://', value: page.fix }, 'url');
      if (page.error === 'scheme-typo') add('url-scheme-typo', 'error', 'Check the start of the address', `It starts with ${code(page.typo)}. Web addresses start with https://.`, { id: 'fix-scheme', label: 'Use https://', value: page.fix }, 'url');
      if (page.error === 'text-before') add('url-text-before', 'error', 'Paste only the address', `The box has ${quote(shorten(page.before))} before the address. Only the address can go in a link.`, { id: 'use-address', label: `Use ${page.fix}`, value: page.fix }, 'url');
      if (page.error === 'scheme') add('url-scheme', 'error', 'Use a web page address', `UTM tags only work on links to web pages. This address starts with ${code(`${page.scheme}:`)}, not https://.`, undefined, 'url');
      if (page.error === 'invalid') add('url-invalid', 'error', 'Check the page address', 'This doesn’t look like a web address. Copy it from your browser’s address bar, starting with https://.', undefined, 'url');
    }
    if (result.use === 'onsite') {
      add('onsite', 'warn', 'Don’t tag links on your own site', 'The shopper is already on your store, brought there by an email, ad or search. A tagged link here records a new campaign click partway through the visit, so reports that credit the last click give the sale to this pop-up or banner instead. Use the plain link. If it points to a different website, choose “Another website” above.', page.ok ? { id: 'copy-plain', label: 'Copy link without tags' } : undefined);
    }
    if (result.missing.length) {
      const words = result.missing.map(name => MISSING_WORDS[name]);
      add('missing', 'error', `Add ${joinWords(words)}`, `Google recommends setting source, medium and campaign on every tagged link: a missing one shows as “(not set)” in reports.<span class="plat-shopify"> ${SHOPIFY_CAMPAIGN}.</span>`);
    }
    if (page.ok && page.joined) {
      const { first, second } = page.joined;
      add('two-urls', 'warn', 'Two addresses run together', `A second ${code(second)} starts partway through, so two links were probably pasted together. Keep the first one, or paste the one you want.`, first ? { id: 'use-first-address', label: `Use ${first}`, value: first } : undefined);
    }
    const tracking = page.ok ? trackingGroups(page) : new Map();
    const removeIds = (group, entries) => {
      const keys = entries.map(entry => entry.key);
      return { id: `remove-${group}-ids`, label: `Remove ${joinWords(keys)}`, value: withoutClickIds(address, keys) };
    };
    if (tracking.has('subscriber')) {
      const entries = tracking.get('subscriber');
      const names = joinWords(entries.map(entry => `${code(entry.key)} (${esc(entry.by)})`));
      add('subscriber-id', 'warn', `Remove ${esc(joinWords(entries.map(entry => entry.key)))} from the address`, `${names} ${entries.length === 1 ? 'identifies' : 'identify'} one subscriber, copied from a link in a message sent to them. Everyone who clicks this link would be tracked as that person.`, removeIds('subscriber', entries));
    }
    if (tracking.has('ad')) {
      const entries = tracking.get('ad');
      const names = joinWords(entries.map(entry => code(entry.key)));
      add('click-id', 'warn', `Remove ${esc(joinWords(entries.map(entry => entry.key)))} from the address`, `${names} ${entries.length === 1 ? 'is a Google ad click ID' : 'are Google ad click IDs'}, copied from a visit that came from an ad. Google Analytics uses auto-tagged values over UTM tags, so visits from this link could be credited to that ad.`, removeIds('ad', entries));
    }
    if (tracking.has('click')) {
      const entries = tracking.get('click');
      const sources = [...new Set(entries.map(entry => entry.by))];
      const by = sources.length === 1
        ? esc(sources[0])
        : joinWords(sources.map(source => `${esc(source)} (${entries.filter(entry => entry.by === source).map(entry => code(entry.key)).join(', ')})`));
      const keys = entries.map(entry => entry.key);
      add('tracking-id', 'info', `${esc(joinWords(keys))} ${keys.length === 1 ? 'is' : 'are'} from a click`, `Added by ${by} when someone clicked. Safe to remove.`, removeIds('click', entries));
    }
    const personal = [];
    tags.forEach(item => {
      // Check what was typed: tidying turns jane@x.com into jane-x.com, which
      // still names a person.
      if (EMAIL_ADDRESS.test(item.typed)) personal.push(`${item.key} contains an email address`);
      else if (item.mergeTags.some(merge => PERSONAL_TAG.test(merge))) personal.push(`${item.key} fills in ${code(item.mergeTags.find(merge => PERSONAL_TAG.test(merge)))}`);
    });
    if (page.ok) page.kept.filter(param => EMAIL_ADDRESS.test(param.value)).forEach(param => personal.push(`${code(param.key)} in your page address contains an email address`));
    if (personal.length) add('personal', 'warn', 'Remove personal details', `${personal.join('; ')}. Google’s policies don’t allow personal information such as email addresses in Analytics, and link tags are collected with each visit.`);
    if (page.ok && page.fragmentTags) add('fragment-tags', 'warn', 'Old tags after the #', 'Your address has utm_ tags after the # sign. Nothing after # is sent to your store’s server, and the new tags already go before it. Delete the old ones from the page address.');
    if (page.ok && page.protocol === 'http') add('http', 'warn', 'Link to https:// if your store has it', 'This address starts with http://. If your store redirects it to https://, the redirect has to keep the tags, and Google notes that redirects can strip UTM parameters.', { id: 'use-https', label: 'Use https://', value: address.replace(/^http:/i, 'https:') });
    if (page.ok && !page.host.includes('.') && page.host !== 'localhost' && !page.host.startsWith('[')) add('host', 'warn', 'Check the domain', `${quote(page.host)} has no ending such as .com, so the link may not open. Check the spelling.`);
    // Two links pasted with a space between them are one problem, not two.
    if (page.ok && page.spaces && !page.joined) add('spaces', 'warn', `The address has ${page.spaces === 1 ? 'a space' : `${page.spaces} spaces`}`, 'A space ends a link in many texts and emails, so it’s written as %20 here. Open the finished link to check it reaches the right page.');
    if (!state.tidy) {
      const upper = tags.filter(item => hasUpper(item.value));
      if (upper.length) add('capitals', 'warn', `Capital letters in ${upper.map(item => item.key).join(', ')}`, 'Google Analytics treats “Sale” and “sale” as different values, so one campaign can split into two rows. Turn on “Tidy values”, or type in lowercase.');
    }
    if (result.use === 'sms' && result.status === 'ready') {
      const { characters, units, doubled, gsm } = result.length;
      const counter = '<a href="/tools/sms-length-calculator">check the whole text</a>';
      const shortening = `Many texting apps shorten links or add their own tracking tags before sending, so ${counter} in the SMS character counter.`;
      const doubles = units !== characters ? `: ${doubled.map(esc).join(' ')} ${doubled.length === 1 ? 'counts' : 'count'} as 2` : '';
      if (!gsm) add('sms-length', 'warn', `This link is ${plural(characters, 'character')}`, `It has a character outside basic SMS text, which switches the whole text to Unicode, with room for 70 per part. ${shortening}`);
      else if (units > 160) add('sms-length', 'warn', `This link alone is ${plural(characters, 'character')}`, `That’s more than the 160 characters that fit in one basic-text SMS part${doubles}, so the text will be sent in parts. ${shortening}`);
      else add('sms-length', 'info', `This link is ${plural(characters, 'character')}`, `In a basic-text SMS, that’s ${units} of the 160 characters that fit in one part${doubles}. ${shortening}`);
    }
    if (page.ok && page.removed.length) {
      const old = page.removed.map(param => code(`${param.key}=${param.value}`));
      add('replaced', 'info', page.removed.length === 1 ? 'Replaced an old tag' : 'Replaced the old tags', `Your page address already had ${joinWords(old)}. The link carries only the new tags, so reports get one set.`);
    }
    const escapes = [];
    tags.forEach(item => item.escapes.forEach(entry => { if (!escapes.some(seen => seen.char === entry.char)) escapes.push(entry); }));
    if (escapes.length) add('encoded', 'info', `Encoded ${plural(escapes.length, 'character')}`, `${escapes.map(entry => `${esc(entry.name)} → ${code(entry.encoded)}`).join(', ')}, so ${escapes.length === 1 ? 'it’s' : 'they’re'} read as part of the value instead of breaking the link.`);
    const merges = [...new Set(tags.flatMap(item => item.mergeTags))];
    if (merges.length) add('merge-tags', 'info', 'Merge tags left as typed', `Your sending app fills in ${joinWords(merges.map(code))} when it sends, so the final link, its channel and its length can differ. Check the app’s preview.`);
    // Tracking IDs have their own check above, so "kept" lists only the
    // store's own parameters.
    const kept = page.ok ? page.kept.filter(param => !trackingId(param.key)) : [];
    if (kept.length) add('kept', 'ok', `Kept ${plural(kept.length, 'parameter')} from your page address`, `${joinWords(kept.map(param => code(param.raw)))} ${kept.length === 1 ? 'stays' : 'stay'} in the link, before the tags.`);
    if (page.ok && page.fragment) add('fragment', 'ok', `Kept ${esc(`#${decodePart(page.fragment)}`)} at the end`, 'The part after # has to come last, so the tags go before it and the page still opens at that spot.');
    if (page.ok && !result.missing.length && result.use !== 'onsite') add('complete', 'ok', 'Has a source, medium and campaign', `Google recommends all three on every tagged link<span class="plat-shopify">, and ${SHOPIFY_CAMPAIGN}</span>.`);
    return checks.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  }

  // ---- Markup shared by the server render and the browser --------------

  function linkMarkup(result) {
    if (!result.url) return `<span class="utm-link-empty">${result.page.error === 'empty' ? 'Add your page address to see your link here.' : 'Fix the page address to see your link here.'}</span>`;
    const { page, tags } = result;
    const breakable = value => esc(value).replace(/(\/|&amp;|=)/g, '$1<wbr>');
    let separator = '?';
    const piece = raw => { const out = `${separator === '?' ? '?' : '&amp;'}<wbr>${breakable(raw)}`; separator = '&'; return out; };
    const kept = page.kept.map(param => piece(param.raw)).join('');
    const added = tags.filter(tag => tag.value).map(tag => {
      const prefix = separator === '?' ? '?' : '&amp;';
      separator = '&';
      return `<span class="utm-link-tag">${prefix}<wbr>${esc(tag.key)}=<wbr><b>${breakable(tag.encoded)}</b></span>`;
    }).join('');
    const fragment = page.fragment ? `<span class="utm-link-extra"><wbr>#${breakable(page.fragment)}</span>` : '';
    return `<span class="utm-link-base">${breakable(page.base)}</span>${kept ? `<span class="utm-link-extra">${kept}</span>` : ''}${added}${fragment}`;
  }

  function lengthText(result) {
    return result.length ? plural(result.length.characters, 'character') : '';
  }

  function channelMarkup(result) {
    const { channel, expected, channelState, use } = result;
    const label = '<p class="utm-channel-label">In Google Analytics 4</p>';
    if (channelState === 'waiting') return `${label}<p class="utm-channel-name">Add a source and medium to see the likely channel.</p>`;
    // On-site links get the warning in the checks instead of a channel.
    if (channelState === 'onsite') return '';
    const reason = channelReason(channel);
    let detail = `${/^utm_/.test(reason) ? reason : reason.charAt(0).toUpperCase() + reason.slice(1)}.`;
    if (channelState === 'mismatch') detail += ` ${channelFix(channel, expected, use)}`;
    if (channel.name === 'Unassigned') detail += use === 'print' ? ` GA4 has no default channel for QR codes or print. Find these visits by Session medium in Traffic acquisition.` : ' Visits still show under their source and medium, just not in a named channel.';
    if (channel.name === 'Paid Other' || channel.name === 'Display') detail += ' That channel is meant for paid ads.';
    const merges = result.tags.slice(0, 3).some(tag => tag.mergeTags.length);
    if (merges) detail += ' Merge tags are read as typed, so the filled-in values may land elsewhere.';
    return `${label}<p class="utm-channel-name">Likely channel: <strong>${esc(channel.name)}</strong></p><p class="utm-channel-reason">${detail}</p>`;
  }

  const LEVEL_WORDS = { error: 'To fix', warn: 'Check', info: 'Note', ok: 'OK' };

  const actionMarkup = action => (action ? `<button type="button" class="utm-fix" data-utm-action="${action.id}"${action.value == null ? '' : ` data-utm-value="${esc(action.value)}"`}>${esc(action.label)}</button>` : '');

  function checksMarkup(result) {
    return result.checks.filter(check => check.field !== 'url').map(check => `<li class="utm-check" data-level="${check.level}" data-check="${check.id}"><span class="utm-check-icon" aria-hidden="true"></span><div><p class="utm-check-title"><span class="utm-visually-hidden">${LEVEL_WORDS[check.level]}: </span>${check.title}</p><p class="utm-check-detail">${check.detail}</p>${actionMarkup(check.action)}</div></li>`).join('');
  }

  // A problem with the typed address is shown under that field, where the
  // fix is, instead of in the result column.
  function urlErrorMarkup(result) {
    const check = result.checks.find(item => item.field === 'url');
    return check ? `<p class="utm-field-error-title">${check.title}</p><p>${check.detail}</p>${actionMarkup(check.action)}` : '';
  }

  // The line beside Copy: why it's disabled, or that the address is still
  // the example, so nobody copies a link to the demo store by mistake.
  function copyHint(result) {
    if (result.status === 'ready') return text(result.state.url).trim() === DEFAULTS.url ? 'Example address\u00a0— paste your own page above.' : '';
    if (result.page.error === 'empty') return 'Add your page address to copy the link.';
    if (result.status === 'invalid') return 'Fix the page address to copy the link.';
    return `${plainText(result.checks.find(check => check.id === 'missing').title)} to copy the link.`;
  }

  const sourceHelp = use => SOURCE_HELP[use] || SOURCE_HELP.email;

  // The value a field puts in the link, shown under it when that differs
  // from what was typed ("Cleanser Restock" goes in as cleanser-restock).
  function fieldNote(tag) {
    return tag.value && tag.encoded !== tag.typed ? `In the link: ${code(tag.encoded)}` : '';
  }

  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
  const plainText = html => String(html).replace(/<[^>]+>/g, '').replace(/&(?:amp|lt|gt|quot|#39);/g, entity => ENTITIES[entity]);

  // One short sentence for screen readers after typing pauses; the link
  // itself is on the page to read or copy.
  // A channel other than the one the link is for (Organic Shopping for an
  // email) is said, and counted as a thing to check, like the amber box.
  function summaryText(result) {
    if (result.status === 'invalid') return `${plainText(result.checks[0].title)}.`;
    if (result.status === 'incomplete') return `${plainText(result.checks.find(check => check.id === 'missing').title)} to finish the link.`;
    const mismatch = result.channelState === 'mismatch';
    const warnings = result.checks.filter(check => check.level === 'warn').length + (mismatch ? 1 : 0);
    const channel = result.channelState === 'onsite' ? '' : ` Likely channel: ${result.channel.name}${mismatch ? `, not ${result.expected}` : ''}.`;
    return `Link ready, ${lengthText(result)}.${channel}${warnings ? ` ${plural(warnings, 'thing')} to check.` : ''}`;
  }

  // Remove tracking IDs from a typed address, keeping everything else exactly
  // as typed: the named keys, or every ID in TRACKING_IDS.
  function withoutClickIds(address, keys) {
    const only = keys ? new Set(keys.map(key => text(key).trim().toLowerCase())) : null;
    const tracked = key => (only ? only.has(key) : TRACKING_IDS.has(key));
    const typed = text(address);
    const hashAt = typed.indexOf('#');
    const beforeHash = hashAt === -1 ? typed : typed.slice(0, hashAt);
    const queryAt = beforeHash.indexOf('?');
    if (queryAt === -1) return typed;
    const kept = beforeHash.slice(queryAt + 1).split('&').filter(segment => segment && !tracked(decodePart(segment.split('=')[0]).trim().toLowerCase()));
    return beforeHash.slice(0, queryAt) + (kept.length ? `?${kept.join('&')}` : '') + (hashAt === -1 ? '' : typed.slice(hashAt));
  }

  // ---- Page behaviour ----------------------------------------------------

  function init(root) {
    if (!root || root.dataset.utmReady === 'true') return;
    root.dataset.utmReady = 'true';
    const find = name => root.querySelector(`[data-utm-${name}]`);
    const fieldInput = name => root.querySelector(`[data-utm-field="${name}"]`);
    const urlInput = find('url');
    const otherSelect = find('other');
    const tidyBox = find('tidy');
    const copyButton = find('copy');
    const status = find('status');
    const fallback = find('fallback');
    const fallbackText = find('fallback-text');
    const announcer = find('announcement');
    if (!urlInput || !otherSelect || !tidyBox || !copyButton) return;
    const channelInputs = () => Array.from(root.querySelectorAll('input[name="utm-channel"]'));
    const channelValue = () => (channelInputs().find(input => input.checked) || {}).value || DEFAULTS.channel;
    const useFor = () => (channelValue() === 'other' ? otherSelect.value : channelValue());
    const more = find('more');
    // A browser restoring the form after Back brings typed values with it.
    if (more && (fieldInput('content').value || fieldInput('term').value)) more.open = true;
    let use = useFor();
    let sourceTouched = fieldInput('source').value.trim() !== USES[use].source;
    let result = null;
    let announceTimer = 0;
    let urlErrorTimer = 0;
    let reportedUse = false;

    const toolEvent = name => document.dispatchEvent(new CustomEvent('myuser:tool-event', { detail: { name } }));
    const setStatus = message => { if (status) status.textContent = message; };

    function readState() {
      const state = { url: urlInput.value, channel: channelValue(), other: otherSelect.value, tidy: tidyBox.checked };
      TAGS.forEach(name => { state[name] = fieldInput(name).value; });
      return state;
    }

    // Rewriting unchanged markup would replace a fix button between its
    // mousedown and mouseup (leaving the address field fires "change"), and
    // the click would never arrive.
    const shown = new Map();
    function setHtml(element, html) {
      if (shown.get(element) === html) return;
      shown.set(element, html);
      element.innerHTML = html;
    }

    function showUrlError(html) {
      const box = find('url-error');
      setHtml(box, html);
      box.hidden = !html;
      if (html) urlInput.setAttribute('aria-invalid', 'true');
      else urlInput.removeAttribute('aria-invalid');
    }

    // Typing "solara.ex" is not a mistake yet: a new address error waits for
    // a pause (or leaving the field). A paste, a fix or a change of an error
    // already shown updates at once.
    function render(announce, typingAddress) {
      result = buildLink(readState());
      root.dataset.utmUse = result.use;
      root.dataset.utmState = result.status;
      find('other-row').hidden = result.state.channel !== 'other';
      setHtml(find('link'), linkMarkup(result));
      find('link').dataset.empty = result.url ? 'false' : 'true';
      const channelBox = find('channel-box');
      channelBox.dataset.state = result.channelState;
      const channelHtml = channelMarkup(result);
      setHtml(channelBox, channelHtml);
      channelBox.hidden = !channelHtml;
      setHtml(find('checks'), checksMarkup(result));
      const urlError = urlErrorMarkup(result);
      clearTimeout(urlErrorTimer);
      if (urlError && typingAddress && find('url-error').hidden) urlErrorTimer = setTimeout(() => showUrlError(urlError), 900);
      else showUrlError(urlError);
      copyButton.disabled = result.status !== 'ready';
      find('copy-hint').textContent = copyHint(result);
      const help = root.querySelector('[data-utm-help="source"]');
      if (help) setHtml(help, sourceHelp(result.use));
      result.tags.forEach(tag => {
        const note = root.querySelector(`[data-utm-note="${tag.name}"]`);
        if (!note) return;
        const html = fieldNote(tag);
        setHtml(note, html);
        note.hidden = !html;
      });
      find('clear').disabled = !urlInput.value && ['campaign', 'content', 'term'].every(name => !fieldInput(name).value);
      if (announce && announcer) {
        clearTimeout(announceTimer);
        announceTimer = setTimeout(() => { announcer.textContent = summaryText(result); }, 900);
      }
    }

    function changed(typingAddress) {
      if (!reportedUse) { reportedUse = true; toolEvent('utm_builder_used'); }
      hideFallback();
      setStatus('');
      render(true, typingAddress);
    }

    // A new destination brings its own source and medium. A source typed in
    // by hand survives a switch between Email and SMS (one app often sends
    // both), but not a switch to a social post or a QR code.
    function applyUse(next) {
      const messaging = name => name === 'email' || name === 'sms';
      if (!(sourceTouched && messaging(use) && messaging(next))) {
        fieldInput('source').value = USES[next].source;
        sourceTouched = false;
      }
      fieldInput('medium').value = USES[next].medium;
      use = next;
    }

    function hideFallback() {
      if (fallback) fallback.hidden = true;
    }

    // The tagged link and the plain one report different events, so a copy of
    // the on-site warning's plain link isn't counted as a tagged link.
    async function copy(value, message, eventName) {
      hideFallback();
      try {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(value);
        setStatus(message);
        toolEvent(eventName);
      } catch (_) {
        // Some browsers block the Clipboard API (older Safari, embedded
        // browsers, pages without permission). Put the link in a box, select
        // it and try the legacy copy command before asking for ⌘C / Ctrl+C.
        if (!fallback || !fallbackText) { setStatus('Copy the link from the box above.'); return; }
        fallbackText.value = value;
        fallback.hidden = false;
        fallbackText.focus();
        fallbackText.select();
        fallbackText.setSelectionRange(0, value.length);
        let copied = false;
        try { copied = typeof document.execCommand === 'function' && document.execCommand('copy'); } catch (__) { copied = false; }
        if (copied) {
          fallback.hidden = true;
          copyButton.focus();
          toolEvent(eventName);
        }
        setStatus(copied ? message : 'Couldn’t copy automatically. The link is selected in the box below, so copy it from there.');
      }
    }

    root.addEventListener('input', event => {
      if (event.target === fieldInput('source')) sourceTouched = true;
      if (event.target.matches('input[type="text"]')) changed(event.target === urlInput && /^(insertText|deleteContent)/.test(event.inputType || ''));
    });
    urlInput.addEventListener('change', () => { clearTimeout(urlErrorTimer); showUrlError(urlErrorMarkup(result)); });
    root.addEventListener('change', event => {
      const target = event.target;
      if (target.name === 'utm-channel' || target === otherSelect) applyUse(useFor());
      if (target.name === 'utm-channel' || target === otherSelect || target === tidyBox) changed();
    });
    // The example address is selected the first time the box is focused, so a
    // paste replaces it. A click places the caret on mouseup, after focus,
    // so a click selects it again there.
    let exampleArmed = true;
    let selectOnMouseUp = false;
    const isExample = () => urlInput.value.trim() === DEFAULTS.url;
    function selectExample() {
      urlInput.select();
      try { urlInput.setSelectionRange(0, urlInput.value.length); } catch (_) { /* not selectable */ }
    }
    urlInput.addEventListener('mousedown', () => {
      if (exampleArmed && document.activeElement !== urlInput) selectOnMouseUp = true;
    });
    urlInput.addEventListener('focus', () => {
      if (!exampleArmed) return;
      exampleArmed = false;
      if (isExample()) selectExample();
      else selectOnMouseUp = false;
    });
    urlInput.addEventListener('mouseup', event => {
      if (!selectOnMouseUp) return;
      selectOnMouseUp = false;
      if (!isExample()) return;
      event.preventDefault();
      selectExample();
    });
    urlInput.addEventListener('blur', () => { selectOnMouseUp = false; });

    root.addEventListener('click', event => {
      const action = event.target.closest('[data-utm-action]');
      if (!action) return;
      if (action.dataset.utmAction === 'copy-plain') { copy(result.plainUrl, 'Link without tags copied.', 'utm_plain_link_copied'); return; }
      // Every address fix carries the address it makes (checksFor sets it).
      if (action.dataset.utmValue == null) return;
      urlInput.value = action.dataset.utmValue;
      changed();
      // The button that was pressed is gone now; keep keyboard users nearby.
      urlInput.focus();
    });
    copyButton.addEventListener('click', () => {
      if (result && result.status === 'ready') copy(result.url, 'Link copied.', 'utm_link_copied');
    });
    find('clear').addEventListener('click', () => {
      urlInput.value = '';
      ['campaign', 'content', 'term'].forEach(name => { fieldInput(name).value = ''; });
      fieldInput('source').value = USES[use].source;
      fieldInput('medium').value = USES[use].medium;
      sourceTouched = false;
      changed();
      urlInput.focus();
    });
    window.addEventListener('pagehide', () => { clearTimeout(announceTimer); clearTimeout(urlErrorTimer); });
    render(false);
  }

  return Object.freeze({
    DEFAULTS,
    USES,
    TAGS,
    tidyValue,
    encodeValue,
    encodedCharacters,
    splitMergeTags,
    parsePage,
    sourceCategory,
    ga4Channel,
    channelReason,
    smsLength,
    buildLink,
    withoutClickIds,
    linkMarkup,
    lengthText,
    channelMarkup,
    checksMarkup,
    urlErrorMarkup,
    copyHint,
    sourceHelp,
    fieldNote,
    summaryText,
    plainText,
    esc,
    init
  });
});
